import * as React from "react";
import {
  LoadingSpinner,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity,
  Toggle
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import FormGroup from "@/components/FormGroup";

interface TenantStatus {
  site_id: number;
  site_info: any;
  tenant: any;
  subscription: any;
  v10_subscription: any;
  actions_taken: string[];
  status: string;
}

interface KBInfo {
  configured: boolean;
  hasTenant?: boolean;
  tenant?: {
    Id: string;
    CompanyName: string;
    Domain: string;
    ThemeColor: string;
    EnableLiveChat: boolean;
    LogoUrl: string;
  };
  stats?: {
    articles: number;
    topics: number;
    categories: number;
  };
  publicUrl?: string;
  domainSetup?: {
    status: string;
    instructions: string | null;
  };
  message?: string;
  error?: boolean;
}

interface KBConnectionInfo {
  configured: boolean;
  current?: {
    server: string;
    database: string;
    isAlternate: boolean;
  };
  primary?: {
    server: string;
    database: string;
  };
  alternate?: {
    server: string;
    database: string;
  } | null;
  hasAlternate?: boolean;
  environment?: string;
  error?: string;
}

interface KBMigrationCounts {
  configured: boolean;
  counts?: {
    tenants: number;
    categories: number;
    topics: number;
    articles: number;
    hits: number;
    votes: number;
  };
  error?: string;
}

interface AuthModeConfig {
  siteId: number;
  authMode: string;
  allowLocalLogin: boolean;
  migratedAt?: string;
  migratedBy?: string;
  hasConfig: boolean;
  error?: string;
}

interface MigrationStep {
  id: string;
  label: string;
  detail?: string;
}

const MIGRATION_STEPS: MigrationStep[] = [
  { id: "prod-db", label: "Create production KB database (kb_v20)" },
  { id: "prod-schema", label: "Create tables via EF Core migration" },
  { id: "prod-data", label: "Migrate data from old kb to kb_v20" },
  { id: "prod-verify", label: "Verify row counts match" },
  { id: "prod-connstr", label: "Update production connection string to kb_v20" },
  { id: "reset-password", label: "Reset SQL admin password (rotated during migration)" },
  { id: "remove-firewall", label: "Remove temp firewall rule on kb-velaro-prod" },
  { id: "deploy-v20", label: "Deploy v20 to production" },
  {
    id: "add-domain",
    label: "Add help.velaro.com custom domain to prod app service",
    detail:
      "Azure Portal → velaro-admin-prod → Custom domains → Add custom domain → help.velaro.com"
  },
  {
    id: "update-dns",
    label: "Update DNS CNAME for help.velaro.com",
    detail:
      "EasyDNS → CNAME: help → velaro-admin-prod.azurewebsites.net"
  },
  {
    id: "verify-live",
    label: "Verify https://help.velaro.com/kb/ serves from v20"
  }
];

const STORAGE_KEY = "kb-migration-checklist";

function loadChecklist(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  // Default: first 5 steps are done (completed during migration)
  return {
    "prod-db": true,
    "prod-schema": true,
    "prod-data": true,
    "prod-verify": true,
    "prod-connstr": true
  };
}

function saveChecklist(checklist: Record<string, boolean>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checklist));
  } catch {}
}

export default function DatabaseTool() {
  const api = useApi();
  const { displayToast } = useToastAlert();

  const [siteId, setSiteId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState<TenantStatus | null>(null);
  const [kbInfo, setKbInfo] = React.useState<KBInfo | null>(null);
  const [kbLoading, setKbLoading] = React.useState(false);
  const [kbDomain, setKbDomain] = React.useState("");
  const [kbDomainSaving, setKbDomainSaving] = React.useState(false);

  // Auth mode state
  const [authModeConfig, setAuthModeConfig] = React.useState<AuthModeConfig | null>(null);
  const [authModeLoading, setAuthModeLoading] = React.useState(false);
  const [authModeSaving, setAuthModeSaving] = React.useState(false);
  const [selectedAuthMode, setSelectedAuthMode] = React.useState("V10");
  const [selectedAllowLocal, setSelectedAllowLocal] = React.useState(true);

  // Migration panel state
  const [connInfo, setConnInfo] = React.useState<KBConnectionInfo | null>(null);
  const [connLoading, setConnLoading] = React.useState(false);
  const [migrationCounts, setMigrationCounts] = React.useState<KBMigrationCounts | null>(null);
  const [checklist, setChecklist] = React.useState<Record<string, boolean>>(loadChecklist);
  const [swapping, setSwapping] = React.useState(false);

  // KB relink state
  const [kbTenants, setKbTenants] = React.useState<Array<{ Id: string; CompanyName: string; Domain: string; ArticleCount: number }>>([]);
  const [kbRelinkId, setKbRelinkId] = React.useState("");
  const [kbRelinking, setKbRelinking] = React.useState(false);

  async function checkAndFixSite() {
    if (!siteId) {
      displayToast(Severity.Error, "Please enter a Site ID");
      return;
    }

    setLoading(true);
    try {
      const response = await api.admin.post("DatabaseTool/AutoVerify", {
        siteId: parseInt(siteId)
      });

      const data = await response.json();
      setStatus(data);

      if (data.actions_taken && data.actions_taken.length > 0) {
        const hasChanges = data.actions_taken.some(
          (action: string) =>
            action.includes("Created") || action.includes("✅")
        );

        if (hasChanges) {
          displayToast(Severity.Success, "Site configured successfully!");
        } else {
          displayToast(Severity.Info, "Site already configured correctly");
        }
      }
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function toggleFeature(feature: string, enabled: boolean) {
    if (!siteId) return;

    try {
      await api.admin.post("DatabaseTool/ToggleFeature", {
        siteId: parseInt(siteId),
        feature,
        enabled
      });

      displayToast(
        Severity.Success,
        `${feature} ${enabled ? "enabled" : "disabled"}`
      );

      // Refresh status
      checkAndFixSite();
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    }
  }

  async function fetchKBInfo() {
    if (!siteId) return;
    setKbLoading(true);
    try {
      const response = await api.admin.get(
        `DatabaseTool/KBInfo?siteId=${siteId}`
      );
      const data = await response.json();
      setKbInfo(data);
      setKbDomain(data?.tenant?.Domain ?? "");
    } catch (error: any) {
      console.error("Failed to fetch KB info:", error);
    } finally {
      setKbLoading(false);
    }
  }

  async function fetchAuthMode() {
    if (!siteId) return;
    setAuthModeLoading(true);
    try {
      const response = await api.admin.get(`DatabaseTool/AuthMode/${siteId}`);
      const data = await response.json();
      setAuthModeConfig(data);
      setSelectedAuthMode(data.authMode ?? "V10");
      setSelectedAllowLocal(data.allowLocalLogin ?? true);
    } catch (error: any) {
      console.error("Failed to fetch auth mode:", error);
    } finally {
      setAuthModeLoading(false);
    }
  }

  async function saveAuthMode() {
    if (!siteId) return;
    setAuthModeSaving(true);
    try {
      await api.admin.put(`DatabaseTool/AuthMode/${siteId}`, {
        authMode: selectedAuthMode,
        allowLocalLogin: selectedAllowLocal
      });
      displayToast(Severity.Success, `Auth mode set to ${selectedAuthMode}`);
      fetchAuthMode();
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    } finally {
      setAuthModeSaving(false);
    }
  }

  async function saveKBDomain() {
    if (!kbInfo?.tenant?.Id) return;
    setKbDomainSaving(true);
    try {
      await api.admin.post("DatabaseTool/KBSetDomain", {
        tenantId: kbInfo.tenant.Id,
        domain: kbDomain
      });
      displayToast(Severity.Success, "KB domain updated");
      fetchKBInfo();
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    } finally {
      setKbDomainSaving(false);
    }
  }

  async function fetchConnectionInfo() {
    setConnLoading(true);
    try {
      const response = await api.admin.get("DatabaseTool/KBConnectionInfo");
      const data = await response.json();
      setConnInfo(data);
    } catch (error: any) {
      console.error("Failed to fetch KB connection info:", error);
    } finally {
      setConnLoading(false);
    }
  }

  async function fetchMigrationCounts() {
    try {
      const response = await api.admin.get("DatabaseTool/KBMigrationCounts");
      const data = await response.json();
      setMigrationCounts(data);
    } catch (error: any) {
      console.error("Failed to fetch migration counts:", error);
    }
  }

  async function swapConnection(useAlternate: boolean) {
    setSwapping(true);
    try {
      const response = await api.admin.post("DatabaseTool/KBSwapConnection", {
        useAlternate
      });
      const data = await response.json();
      if (data.success) {
        displayToast(
          Severity.Success,
          `Switched to ${data.current?.database} on ${data.current?.server}`
        );
        fetchConnectionInfo();
        fetchMigrationCounts();
      }
    } catch (error: any) {
      displayToast(Severity.Error, `Swap failed: ${error.message}`);
    } finally {
      setSwapping(false);
    }
  }

  function toggleStep(stepId: string) {
    const updated = { ...checklist, [stepId]: !checklist[stepId] };
    setChecklist(updated);
    saveChecklist(updated);
  }

  async function fetchKBTenants() {
    try {
      const response = await api.admin.get("DatabaseTool/KBTenants");
      const data = await response.json();
      setKbTenants(Array.isArray(data?.tenants) ? data.tenants : []);
    } catch {}
  }

  async function relinkKBTenant() {
    if (!siteId || !kbRelinkId) return;
    setKbRelinking(true);
    try {
      await api.admin.put("DatabaseTool/KBLinkTenant", {
        siteId: parseInt(siteId),
        tenantId: kbRelinkId
      });
      displayToast(Severity.Success, "KB linked successfully");
      fetchKBInfo();
    } catch (error: any) {
      displayToast(Severity.Error, `Failed to link KB: ${error.message}`);
    } finally {
      setKbRelinking(false);
    }
  }

  // Fetch KB info, auth mode, and tenant list when site status is loaded
  React.useEffect(() => {
    if (status && siteId) {
      fetchKBInfo();
      fetchAuthMode();
      fetchKBTenants();
    }
  }, [status]);

  // Fetch connection info on mount
  React.useEffect(() => {
    fetchConnectionInfo();
    fetchMigrationCounts();
  }, []);

  const featureGroups: Array<{
    label: string;
    features: Array<{ key: string; label: string }>;
  }> = [
    {
      label: "Inbound Channels",
      features: [
        { key: "EnableWeb",       label: "Web Messenger" },
        { key: "EnableSms",       label: "SMS" },
        { key: "EnableEmail",     label: "Email" },
        { key: "EnableWhatsapp",  label: "WhatsApp" },
        { key: "EnableFacebook",  label: "Facebook Messenger" },
        { key: "EnableInstagram", label: "Instagram DMs" },
        { key: "EnableTikTok",    label: "TikTok DMs" },
      ]
    },
    {
      label: "AI & Automation",
      features: [
        { key: "EnableAI",                  label: "AI Chatbots" },
        { key: "EnableWorkflowRules",       label: "Workflow Rules" },
        { key: "EnableAutomation",          label: "Automation" },
        { key: "EnableMcp",                 label: "MCP Servers" },
        { key: "EnableQuickStartWizards",   label: "Quick Setup Wizards" },
        { key: "EnableBotVoice",            label: "ElevenLabs Bot Voice (TTS)" },
      ]
    },
    {
      label: "Routing & Queuing",
      features: [
        { key: "EnableTierEscalation", label: "Tier Escalation Routing" },
        { key: "EnablePriorityQueue",  label: "Priority / VIP Queue" },
        { key: "EnableVideoChat",      label: "Video Chat" },
      ]
    },
    {
      label: "Integrations",
      features: [
        { key: "EnableHubSpot",             label: "HubSpot CRM" },
        { key: "EnableBigCommerce",         label: "BigCommerce" },
        { key: "EnableWooCommerce",         label: "WooCommerce" },
        { key: "EnableDynamics",            label: "Microsoft Dynamics 365" },
        { key: "EnableNetsuite",            label: "NetSuite" },
        { key: "EnableNetSuiteSkills",      label: "NetSuite AI Skills" },
        { key: "EnableConversationTraining",label: "Conversation Training (auto-index resolved chats)" },
        { key: "EnableOneDriveAttachments", label: "OneDrive Attachments" },
        { key: "EnableTeamsNotifications",  label: "Teams Notifications" },
        { key: "EnableSlackNotifications",  label: "Slack Notifications" },
        { key: "EnableCalendarBooking",     label: "Calendar Booking" },
      ]
    },
    {
      label: "Platform",
      features: [
        { key: "EnableMultipleDeployments", label: "Multiple Deployments" },
        { key: "EnablePageWidgets",         label: "Page Widgets" },
      ]
    },
    {
      label: "Security & Compliance",
      features: [
        { key: "EnableEnterpriseSso",           label: "Enterprise SSO" },
        { key: "EnableAdvancedMfa",             label: "Advanced MFA" },
        { key: "EnablePasskeys",                label: "Passkeys / FIDO2" },
        { key: "EnableIpBlocking",              label: "Visitor IP Blocking" },
        { key: "EnableAgentIpRestriction",      label: "Agent Login IP Restriction" },
        { key: "EnableSessionTimeout",          label: "Session Timeout" },
        { key: "EnableConcurrentSessionLimit",  label: "Concurrent Session Limit" },
        { key: "EnableDataRetention",           label: "Data Retention (GDPR Purge)" },
        { key: "EnableScheduledPurge",          label: "Scheduled Auto-Purge" },
        { key: "EnableTranscriptStorage",       label: "BYOS Transcript Storage" },
        { key: "EnableMessageScrubbing",        label: "Message Scrubbing (PII)" },
      ]
    },
  ];

  const completedSteps = MIGRATION_STEPS.filter((s) => checklist[s.id]).length;
  const totalSteps = MIGRATION_STEPS.length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 mb-2">
          Database Administration Tool
        </h1>
        <p className="text-slate-600">
          Verify and configure messaging tenant and subscription settings for
          any site
        </p>
      </div>

      {/* Site ID Input */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <FormGroup label="Site ID">
          <div className="flex gap-4">
            <input
              type="number"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="Enter site ID to check"
              className="flex-1 px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <PrimaryBlueButton
              label={loading ? "Checking..." : "Check & Fix"}
              onClick={checkAndFixSite}
              disabled={loading || !siteId}
            />
          </div>
        </FormGroup>

        {loading && (
          <div className="mt-4 flex justify-center">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {/* Status Display */}
      {status && !loading && (
        <>
          {/* Actions Taken */}
          {status.actions_taken && status.actions_taken.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">
                Actions Taken:
              </h3>
              <ul className="list-disc list-inside space-y-1">
                {status.actions_taken.map((action, i) => (
                  <li key={i} className="text-blue-800">
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Site Info */}
            <div
              className={`rounded-lg p-4 border-2 ${
                status.site_info
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <h3 className="font-semibold mb-2">Site Information</h3>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                  status.site_info
                    ? "bg-green-500 text-white"
                    : "bg-red-500 text-white"
                }`}
              >
                {status.site_info ? "Found" : "Not Found"}
              </div>
              {status.site_info && (
                <div className="text-sm space-y-1">
                  <div>
                    <strong>Company:</strong> {status.site_info.CompanyName}
                  </div>
                  <div>
                    <strong>Created:</strong>{" "}
                    {new Date(status.site_info.DateCreated).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>

            {/* Tenant */}
            <div
              className={`rounded-lg p-4 border-2 ${
                status.tenant
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <h3 className="font-semibold mb-2">Messaging Tenant</h3>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                  status.tenant
                    ? "bg-green-500 text-white"
                    : "bg-red-500 text-white"
                }`}
              >
                {status.tenant ? "Exists" : "Missing"}
              </div>
              {status.tenant && (
                <div className="text-sm">
                  <div>
                    <strong>Tenant ID:</strong> {status.tenant.Id}
                  </div>
                </div>
              )}
            </div>

            {/* Subscription */}
            <div
              className={`rounded-lg p-4 border-2 ${
                status.subscription
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
            >
              <h3 className="font-semibold mb-2">Subscription</h3>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                  status.subscription
                    ? "bg-green-500 text-white"
                    : "bg-red-500 text-white"
                }`}
              >
                {status.subscription ? "Exists" : "Missing"}
              </div>
              {status.subscription && (
                <div className="text-sm">
                  <div>
                    <strong>Sub ID:</strong> {status.subscription.Id}
                  </div>
                </div>
              )}
            </div>

            {/* V10 License */}
            <div
              className={`rounded-lg p-4 border-2 ${
                status.v10_subscription?.Status === "Active"
                  ? "bg-green-50 border-green-500"
                  : "bg-yellow-50 border-yellow-500"
              }`}
            >
              <h3 className="font-semibold mb-2">V10 License</h3>
              <div
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                  status.v10_subscription?.Status === "Active"
                    ? "bg-green-500 text-white"
                    : "bg-yellow-500 text-white"
                }`}
              >
                {status.v10_subscription?.Status || "Not Found"}
              </div>
              {status.v10_subscription && (
                <div className="text-sm">
                  <div>
                    <strong>Expires:</strong>{" "}
                    {new Date(
                      status.v10_subscription.ExpirationDate
                    ).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Subscription Settings — grouped by category */}
          {status.subscription && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-1">Subscription Settings</h2>
              <p className="text-sm text-slate-500 mb-5">Toggle features on/off per site. Changes take effect immediately — no save needed.</p>

              {featureGroups.map(group => (
                <div key={group.label} className="mb-6">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">{group.label}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {group.features.map(feature => (
                      <div key={feature.key} className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-slate-50">
                        <span className="text-sm text-slate-700">{feature.label}</span>
                        <Toggle
                          toggled={status.subscription[feature.key] === 1}
                          changeToggle={() => toggleFeature(feature.key, status.subscription[feature.key] !== 1)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Outbound Campaigns — toggles + limits in one place */}
              <div className="mb-2">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Outbound Campaigns</h3>
                <CampaignSettingsCard siteId={parseInt(siteId)} api={api} displayToast={displayToast} />
              </div>
            </div>
          )}

          {/* Retention & Security Limits */}
          {status?.subscription && (
            <RetentionLimitsSection
              siteId={parseInt(siteId)}
              subscription={status.subscription}
              api={api}
              displayToast={displayToast}
              onRefresh={checkAndFixSite}
            />
          )}

          {/* Scraper Quota Limits */}
          {status && siteId && (
            <ScraperLimitsSection
              siteId={parseInt(siteId)}
              api={api}
              displayToast={displayToast}
            />
          )}
        </>
      )}

      {/* Auth Mode Section (per-site) */}
      {status && !loading && (
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <h2 className="text-xl font-bold text-slate-800 mb-1 flex items-center gap-2">
            <i className="fa fa-lock text-indigo-500" />
            Auth Mode (Messaging App)
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Controls how agents log in to the messaging/workspace app. The admin app always uses Microsoft (Entra) login.
          </p>

          {authModeLoading ? (
            <div className="flex justify-center py-4"><LoadingSpinner /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { value: "V10", label: "Password Only", desc: "Email + password via V10 (legacy)", color: "slate" },
                  { value: "Dual", label: "Both Options", desc: "Agents can use password OR Microsoft login", color: "blue" },
                  { value: "EntraId", label: "Microsoft Only", desc: "Entra / Microsoft login required — no password", color: "indigo" }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedAuthMode(opt.value)}
                    className={`text-left p-4 rounded-lg border-2 transition-all ${
                      selectedAuthMode === opt.value
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="font-semibold text-slate-800 text-sm">{opt.label}</div>
                    <div className="text-xs text-slate-500 mt-1">{opt.desc}</div>
                    {authModeConfig?.authMode === opt.value && (
                      <div className="text-xs text-green-600 font-medium mt-1">Currently active</div>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between p-3 border border-slate-200 rounded-md">
                <div>
                  <span className="text-slate-700 text-sm font-medium">Allow local password login</span>
                  <div className="text-xs text-slate-400">When off, only Microsoft/SSO login is accepted even in Dual mode</div>
                </div>
                <Toggle
                  toggled={selectedAllowLocal}
                  changeToggle={() => setSelectedAllowLocal(!selectedAllowLocal)}
                />
              </div>

              {authModeConfig?.migratedAt && (
                <div className="text-xs text-slate-400">
                  Migrated to EntraId on {new Date(authModeConfig.migratedAt).toLocaleDateString()} by {authModeConfig.migratedBy}
                </div>
              )}

              <div className="flex items-center gap-3">
                <PrimaryBlueButton
                  label={authModeSaving ? "Saving..." : "Save Auth Mode"}
                  onClick={saveAuthMode}
                  disabled={authModeSaving}
                />
                {authModeConfig && (
                  <span className="text-xs text-slate-400">
                    Current: <strong>{authModeConfig.authMode}</strong>
                    {!authModeConfig.hasConfig && " (default — not set)"}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* KB Domain & Info Section (per-site) */}
      {status && !loading && kbInfo && (
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="fa fa-book text-blue-500" />
            Knowledge Base
          </h2>

          {kbLoading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : !kbInfo.configured ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                {kbInfo.message ||
                  "KB database not configured on this environment."}
              </p>
            </div>
          ) : !kbInfo.hasTenant ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
              <p className="text-sm text-yellow-800 font-medium">
                No KB tenant linked to this site.
              </p>
              <p className="text-xs text-yellow-700">
                Customer can visit <code className="bg-yellow-100 px-1 rounded">/kb/setup</code> in admin to create a new one,
                or link to an existing tenant below.
              </p>
              {kbTenants.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-yellow-900">
                    Link to existing KB tenant:
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={kbRelinkId}
                      onChange={(e) => setKbRelinkId(e.target.value)}
                      className="flex-1 text-sm px-2 py-1.5 border border-yellow-300 rounded bg-white text-slate-800"
                    >
                      <option value="">Select tenant...</option>
                      {kbTenants.map((t) => (
                        <option key={t.Id} value={t.Id}>
                          {t.CompanyName} {t.Domain ? `(${t.Domain})` : ""} — {t.ArticleCount} articles
                        </option>
                      ))}
                    </select>
                    <PrimaryBlueButton
                      label={kbRelinking ? "Linking..." : "Link"}
                      onClick={relinkKBTenant}
                      disabled={kbRelinking || !kbRelinkId}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Tenant Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Company</div>
                  <div className="text-sm font-medium">
                    {kbInfo.tenant?.CompanyName ?? "\u2014"}
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Tenant ID</div>
                  <div className="text-xs font-mono break-all">
                    {kbInfo.tenant?.Id ?? "\u2014"}
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Content</div>
                  <div className="text-sm font-medium">
                    {kbInfo.stats?.articles ?? 0} articles,{" "}
                    {kbInfo.stats?.topics ?? 0} topics,{" "}
                    {kbInfo.stats?.categories ?? 0} categories
                  </div>
                </div>
              </div>

              {/* Domain Setup */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <i className="fa fa-globe text-green-500" />
                  Custom Domain
                </h3>

                {kbInfo.tenant?.Domain ? (
                  <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                      <span className="text-sm font-medium text-green-800">
                        Active:
                      </span>
                      <a
                        href={`https://${kbInfo.tenant.Domain}/kb/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-green-700 hover:underline"
                      >
                        https://{kbInfo.tenant.Domain}/kb/
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      No custom domain configured. KB is accessible via tenant
                      GUID URL only.
                    </p>
                  </div>
                )}

                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">
                      Domain (e.g., help.acme.com)
                    </label>
                    <input
                      type="text"
                      value={kbDomain}
                      onChange={(e) => setKbDomain(e.target.value)}
                      placeholder="help.customer.com"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <PrimaryBlueButton
                    label={kbDomainSaving ? "Saving..." : "Set Domain"}
                    onClick={saveKBDomain}
                    disabled={kbDomainSaving || !kbDomain}
                  />
                </div>

                {/* Setup Instructions */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-xs font-semibold text-blue-900 mb-2">
                    Domain Setup Checklist
                  </h4>
                  <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                    <li>
                      Customer creates CNAME:{" "}
                      <code className="bg-blue-100 px-1 rounded">
                        help.customer.com
                      </code>{" "}
                      &rarr;{" "}
                      <code className="bg-blue-100 px-1 rounded">
                        velaro-admin-prod.azurewebsites.net
                      </code>
                    </li>
                    <li>
                      Customer creates TXT record:{" "}
                      <code className="bg-blue-100 px-1 rounded">
                        asuid.help
                      </code>{" "}
                      &rarr; Azure verification hash (get from Azure Portal)
                    </li>
                    <li>
                      Add hostname in Azure Portal &rarr; App Service &rarr;
                      Custom domains
                    </li>
                    <li>Azure auto-provisions free SSL certificate</li>
                    <li>Set the domain in the field above</li>
                    <li>
                      Verify: visit{" "}
                      <code className="bg-blue-100 px-1 rounded">
                        https://help.customer.com/kb/
                      </code>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions (shown when no site loaded) */}
      {!status && !loading && (
        <div className="bg-slate-50 rounded-lg p-6 text-slate-600 mb-6">
          <h3 className="font-semibold text-slate-800 mb-2">How to use:</h3>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter a Site ID above</li>
            <li>
              Click &quot;Check &amp; Fix&quot; to verify the site configuration
            </li>
            <li>
              If tenant or subscription is missing, they will be created
              automatically
            </li>
            <li>Toggle feature flags as needed</li>
            <li>All changes are safe and only affect the specified site</li>
          </ol>
        </div>
      )}

      {/* ═══════════ KB Production Migration Panel ═══════════ */}
      <div className="mt-8 border-t-2 border-slate-200 pt-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-slate-800 mb-1 flex items-center gap-2">
            <i className="fa fa-database text-indigo-500" />
            KB Production Migration
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            Track migration progress and manage KB database connections
          </p>

          {/* Connection Status */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              Current KB Database Connection
            </h3>
            {connLoading ? (
              <LoadingSpinner />
            ) : connInfo?.configured ? (
              <div className="flex flex-col gap-3">
                <div
                  className={`p-4 rounded-lg border-2 ${
                    connInfo.current?.isAlternate
                      ? "bg-amber-50 border-amber-400"
                      : "bg-green-50 border-green-400"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${
                            connInfo.current?.isAlternate
                              ? "bg-amber-500"
                              : "bg-green-500"
                          }`}
                        />
                        <span className="text-sm font-semibold">
                          Connected to:{" "}
                          <span className="font-mono">
                            {connInfo.current?.database}
                          </span>
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            connInfo.current?.isAlternate
                              ? "bg-amber-200 text-amber-800"
                              : "bg-green-200 text-green-800"
                          }`}
                        >
                          {connInfo.current?.isAlternate
                            ? "Staging DB"
                            : "Production DB"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 font-mono">
                        {connInfo.current?.server}
                      </div>
                    </div>
                    {connInfo.hasAlternate && (
                      <SecondaryBlueButton
                        label={
                          swapping
                            ? "Switching..."
                            : connInfo.current?.isAlternate
                            ? "Switch to Production KB"
                            : "Switch to Staging KB"
                        }
                        onClick={() =>
                          swapConnection(!connInfo.current?.isAlternate)
                        }
                        disabled={swapping}
                      />
                    )}
                  </div>
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    <strong>Persists across restarts.</strong> During migration, staging
                    points to prod KB so you can edit help articles before v20 is live.
                    Once v20 is deployed to production, switch staging back to its own KB
                    here. You can always flip back temporarily if you need to fix prod
                    KB data from staging.
                  </div>
                </div>

                {/* Show both connection options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div
                    className={`p-3 rounded-lg border ${
                      !connInfo.current?.isAlternate
                        ? "border-green-300 bg-green-50"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="text-xs text-slate-500 mb-1">
                      Primary (Production KB)
                    </div>
                    <div className="text-sm font-mono">
                      {connInfo.primary?.database}
                    </div>
                    <div className="text-xs text-slate-400 font-mono">
                      {connInfo.primary?.server}
                    </div>
                    {!connInfo.current?.isAlternate && (
                      <span className="text-xs text-green-600 font-medium">
                        Active
                      </span>
                    )}
                  </div>
                  {connInfo.hasAlternate && connInfo.alternate && (
                    <div
                      className={`p-3 rounded-lg border ${
                        connInfo.current?.isAlternate
                          ? "border-amber-300 bg-amber-50"
                          : "border-slate-200 bg-slate-50"
                      }`}
                    >
                      <div className="text-xs text-slate-500 mb-1">
                        Alternate (Staging KB)
                      </div>
                      <div className="text-sm font-mono">
                        {connInfo.alternate.database}
                      </div>
                      <div className="text-xs text-slate-400 font-mono">
                        {connInfo.alternate.server}
                      </div>
                      {connInfo.current?.isAlternate && (
                        <span className="text-xs text-amber-600 font-medium">
                          Active
                        </span>
                      )}
                    </div>
                  )}
                  {!connInfo.hasAlternate && (
                    <div className="p-3 rounded-lg border border-dashed border-slate-300 bg-slate-50">
                      <div className="text-xs text-slate-500 mb-1">
                        Alternate (Staging KB)
                      </div>
                      <div className="text-xs text-slate-400 italic">
                        Not configured. Set{" "}
                        <code className="bg-slate-200 px-1 rounded">
                          KnowledgeBaseConnectionString_Staging
                        </code>{" "}
                        app setting to enable switching.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                {connInfo?.error ??
                  "KB database not configured on this environment."}
              </div>
            )}
          </div>

          {/* Data Verification */}
          {migrationCounts?.configured && migrationCounts.counts && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">
                Current KB Data Counts
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(migrationCounts.counts).map(([key, count]) => (
                  <div
                    key={key}
                    className="p-3 bg-slate-50 rounded-lg text-center"
                  >
                    <div className="text-lg font-bold text-slate-800">
                      {(count as number).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500 capitalize">
                      {key}
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={fetchMigrationCounts}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                Refresh counts
              </button>
            </div>
          )}

          {/* Migration Checklist */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">
                Migration Checklist
              </h3>
              <span className="text-xs text-slate-500">
                {completedSteps}/{totalSteps} complete
              </span>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
              <div
                className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${(completedSteps / totalSteps) * 100}%`
                }}
              />
            </div>
            <div className="space-y-2">
              {MIGRATION_STEPS.map((step) => {
                const done = !!checklist[step.id];
                return (
                  <div
                    key={step.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                      done
                        ? "bg-slate-50 border-slate-200"
                        : "bg-white border-slate-300"
                    }`}
                  >
                    <div className="pt-0.5">
                      <Toggle
                        toggled={done}
                        changeToggle={() => toggleStep(step.id)}
                      />
                    </div>
                    <div className="flex-1">
                      <div
                        className={`text-sm ${
                          done
                            ? "text-slate-400 line-through"
                            : "text-slate-700"
                        }`}
                      >
                        {step.label}
                      </div>
                      {step.detail && !done && (
                        <div className="text-xs text-slate-400 mt-1 font-mono">
                          {step.detail}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* DNS Cutover Instructions */}
          <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50">
            <h3 className="text-sm font-semibold text-indigo-900 mb-3 flex items-center gap-2">
              <i className="fa fa-exchange text-indigo-500" />
              DNS Cutover for help.velaro.com
            </h3>
            <div className="space-y-3">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="text-xs font-semibold text-red-800 mb-2">
                  Important: Hostname is currently bound to v10
                  (kb-us-east-prod). These 3 steps must happen together:
                </div>
                <ol className="text-xs text-red-700 space-y-1 list-decimal list-inside">
                  <li>
                    Remove{" "}
                    <code className="bg-red-100 px-1 rounded">
                      help.velaro.com
                    </code>{" "}
                    from{" "}
                    <code className="bg-red-100 px-1 rounded">
                      kb-us-east-prod
                    </code>{" "}
                    (Azure Portal &rarr; Custom domains &rarr; Delete)
                  </li>
                  <li>
                    Add{" "}
                    <code className="bg-red-100 px-1 rounded">
                      help.velaro.com
                    </code>{" "}
                    to{" "}
                    <code className="bg-red-100 px-1 rounded">
                      velaro-admin-prod
                    </code>{" "}
                    (Azure Portal &rarr; Custom domains &rarr; Add). TXT
                    verification record already exists.
                  </li>
                  <li>
                    Update CNAME in EasyDNS:{" "}
                    <code className="bg-red-100 px-1 rounded">help</code>{" "}
                    &rarr;{" "}
                    <code className="bg-red-100 px-1 rounded">
                      velaro-admin-prod.azurewebsites.net
                    </code>
                  </li>
                </ol>
              </div>
              <div className="p-3 bg-white rounded-lg border border-indigo-200">
                <div className="text-xs text-slate-500 mb-1">
                  Current DNS (v10 — old system)
                </div>
                <div className="text-sm font-mono">
                  CNAME: <strong>help</strong> &rarr;{" "}
                  <code className="bg-slate-100 px-1 rounded">
                    kb-us-east-prod.azurewebsites.net
                  </code>
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-indigo-200">
                <div className="text-xs text-slate-500 mb-1">
                  Target DNS (v20 production)
                </div>
                <div className="text-sm font-mono">
                  CNAME: <strong>help</strong> &rarr;{" "}
                  <code className="bg-green-100 px-1 rounded">
                    velaro-admin-prod.azurewebsites.net
                  </code>
                </div>
              </div>
              <div className="text-xs text-indigo-700 space-y-1">
                <p>
                  <strong>TXT record:</strong>{" "}
                  <code className="bg-indigo-100 px-1 rounded">
                    asuid.help.velaro.com
                  </code>{" "}
                  already exists in EasyDNS &mdash; keep it permanently.
                </p>
                <p>
                  <strong>DNS Provider:</strong> EasyDNS (velaro.com zone)
                </p>
                <p>
                  <strong>After cutover:</strong> Set the Velaro KB tenant
                  Domain to{" "}
                  <code className="bg-indigo-100 px-1 rounded">
                    help.velaro.com
                  </code>{" "}
                  in the KB tenant record (use the domain field above or via
                  /kb/settings).
                </p>
                <p>
                  <strong>Verify:</strong> Visit help.velaro.com and check for{" "}
                  <code className="bg-indigo-100 px-1 rounded">v20</code> badge
                  in footer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Retention & Security Limits section (inside DatabaseTool)
// ─────────────────────────────────────────────────────────────────

/** Green badge = actively blocks/enforces. Amber = stored, not yet enforced in code. */
function EnforcedBadge({ enforced }: { enforced: boolean }) {
  return enforced
    ? <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700">ENFORCED</span>
    : <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">STORED ONLY</span>;
}

function LimitField({ label, hint, value, min, max, onChange, enforced = true, readOnly = false }: {
  label: string; hint: string; value: number; min: number; max: number;
  onChange: (v: number) => void; enforced?: boolean; readOnly?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label}
        <EnforcedBadge enforced={enforced} />
      </label>
      <input type="number" min={min} max={max} value={value} readOnly={readOnly}
        onChange={e => !readOnly && onChange(parseInt(e.target.value) || min)}
        className={`w-full px-3 py-2 text-sm border border-slate-300 rounded-lg ${readOnly ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}`} />
      <p className="text-xs text-slate-400 mt-1">{hint}</p>
    </div>
  );
}

// ── Reindex Now button ────────────────────────────────────────────────────────
function ReindexNowButton({ siteId, api, displayToast }: {
  siteId: number; api: ReturnType<typeof useApi>;
  displayToast: (severity: any, message: string) => void;
}) {
  const [reindexing, setReindexing] = React.useState(false);
  const [result, setResult] = React.useState<{ articles: number; scraperPages: number } | null>(null);

  const reindex = async () => {
    setReindexing(true);
    setResult(null);
    try {
      const resp = await api.admin.post(`DatabaseTool/ReindexKb/${siteId}`, {});
      const data = await resp.json();
      if (data.success) {
        setResult({ articles: data.articles ?? 0, scraperPages: data.scraperPages ?? 0 });
        displayToast("success", `Reindexed: ${data.articles} KB articles, ${data.scraperPages} scraper pages`);
      } else {
        displayToast("warning", data.message ?? "Reindex ran but reported no results — Azure Search may not be configured on this environment.");
      }
    } catch {
      displayToast("error", "Reindex request failed.");
    } finally {
      setReindexing(false);
    }
  };

  return (
    <div className="flex items-center gap-3 mt-2 mb-2">
      <button onClick={reindex} disabled={reindexing || !siteId}
        className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50">
        <i className={`fa-solid fa-rotate ${reindexing ? "animate-spin" : ""} text-xs`} />
        {reindexing ? "Reindexing…" : "Reindex Now"}
      </button>
      {result && (
        <span className="text-xs text-slate-500">
          Done: <strong>{result.articles}</strong> KB articles, <strong>{result.scraperPages}</strong> scraper pages
        </span>
      )}
    </div>
  );
}

// ── Outbound Campaigns card — toggles + limits in one place ──────────────────
function CampaignSettingsCard({ siteId, api, displayToast }: {
  siteId: number; api: ReturnType<typeof useApi>;
  displayToast: (severity: any, message: string) => void;
}) {
  const [enableEmail, setEnableEmail]   = React.useState(false);
  const [enableSms, setEnableSms]       = React.useState(false);
  const [enableIvr, setEnableIvr]       = React.useState(false);
  const [maxContacts, setMaxContacts]   = React.useState(500);
  const [maxEmails, setMaxEmails]       = React.useState(5000);
  const [maxCallsPerMonth, setMaxCallsPerMonth] = React.useState(0);
  const [loaded, setLoaded]             = React.useState(false);
  const [saving, setSaving]             = React.useState(false);

  React.useEffect(() => {
    async function load() {
      try {
        const [cr, sr] = await Promise.all([
          api.admin.get(`DatabaseTool/CampaignSettings/${siteId}`),
          api.admin.get(`DatabaseTool/SiteSubscription/${siteId}`)
        ]);
        const cd = await cr.json();
        setEnableEmail(cd.enableOutboundCampaigns ?? false);
        setEnableSms(cd.enableSmsOutbound ?? false);
        setEnableIvr(cd.enableOutboundIvr ?? false);
        setMaxCallsPerMonth(cd.maxCallsPerMonth ?? 0);
        // limits come from subscription row
        const sd = await sr.json();
        setMaxContacts(sd?.MaxContacts ?? 500);
        setMaxEmails(sd?.MaxOutboundEmailsPerMonth ?? 5000);
      } catch {}
      finally { setLoaded(true); }
    }
    load();
  }, [siteId]);

  const save = async () => {
    setSaving(true);
    try {
      const resp = await api.admin.put("DatabaseTool/RetentionLimits", {
        siteId,
        enableOutboundCampaigns: enableEmail,
        enableSmsOutbound: enableSms,
        enableOutboundIvr: enableIvr,
        maxContacts,
        maxOutboundEmailsPerMonth: maxEmails,
        maxCallsPerMonth,
      });
      if (resp.ok) displayToast("success", "Campaign settings saved.");
      else { const e = await resp.json(); displayToast("error", e.error ?? "Save failed."); }
    } catch { displayToast("error", "Save failed."); }
    finally { setSaving(false); }
  };

  if (!loaded) return <div className="text-sm text-slate-400 py-2">Loading…</div>;

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white">
          <div>
            <div className="text-sm font-medium text-slate-700">Email Campaigns</div>
            <div className="text-xs text-slate-400">Unlocks /email-campaigns page + broadcast sends</div>
          </div>
          <Toggle toggled={enableEmail} changeToggle={() => setEnableEmail(v => !v)} />
        </div>
        <div className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white">
          <div>
            <div className="text-sm font-medium text-slate-700">SMS Campaigns</div>
            <div className="text-xs text-slate-400">Requires dedicated phone number via ACS</div>
          </div>
          <Toggle toggled={enableSms} changeToggle={() => setEnableSms(v => !v)} />
        </div>
        <div className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white">
          <div>
            <div className="text-sm font-medium text-slate-700">Outbound IVR Calling</div>
            <div className="text-xs text-slate-400">AI-voiced outbound calls via Twilio + ElevenLabs. Requires TCPA terms acceptance.</div>
          </div>
          <Toggle toggled={enableIvr} changeToggle={() => setEnableIvr(v => !v)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Max Contacts</label>
          <input type="number" min={0} value={maxContacts}
            onChange={e => setMaxContacts(parseInt(e.target.value) || 0)}
            className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm" />
          <p className="text-xs text-slate-400 mt-0.5">0 = unlimited. Default 500. Blocks CSV import when exceeded.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Max Emails / Month</label>
          <input type="number" min={0} value={maxEmails}
            onChange={e => setMaxEmails(parseInt(e.target.value) || 0)}
            className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm" />
          <p className="text-xs text-slate-400 mt-0.5">0 = unlimited. Default 5,000. Blocks sends when exceeded.</p>
          {maxEmails > 0 && (
            <div className="mt-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-100 rounded text-xs">
              <span className="text-blue-600 font-medium">
                ACS cost: ~${(maxEmails * 0.00025).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
              </span>
              <span className="text-blue-400 ml-2">($0.00025 per email · ¼ cent each)</span>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Max IVR Calls / Month</label>
          <input type="number" min={0} value={maxCallsPerMonth}
            onChange={e => setMaxCallsPerMonth(parseInt(e.target.value) || 0)}
            className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm" />
          <p className="text-xs text-slate-400 mt-0.5">0 = unlimited. Default 0 (disable). Shown as quota bar in IVR page.</p>
        </div>
      </div>
      <button onClick={save} disabled={saving}
        className="px-4 py-1.5 bg-cornflower-blue-500 text-white text-sm font-medium rounded hover:bg-cornflower-blue-600 disabled:opacity-50">
        {saving ? "Saving…" : "Save Campaign Settings"}
      </button>

      <AcsCostReference />
      <AzureQuickLinks />
    </div>
  );
}

// ── Azure Quick Links — jump directly to ACS, App Insights, Event Grid from superadmin ──
function AzureQuickLinks() {
  const [open, setOpen] = React.useState(false);

  const links = {
    staging: [
      {
        label: "ACS Communication Services (staging)",
        desc: "velaro-sms · v10 subscription",
        url: "https://portal.azure.com/#resource/subscriptions/bf6c4494-5e1e-434e-97bf-2a92954c80c9/resourceGroups/Default-Web-EastUS/providers/Microsoft.Communication/CommunicationServices/velaro-sms/overview",
        icon: "📡"
      },
      {
        label: "ACS Email Service (staging)",
        desc: "velaro-email · v10 subscription",
        url: "https://portal.azure.com/#resource/subscriptions/bf6c4494-5e1e-434e-97bf-2a92954c80c9/resourceGroups/Default-Web-EastUS/providers/Microsoft.Communication/emailServices/velaro-email/overview",
        icon: "📧"
      },
      {
        label: "App Insights — Campaign queries (staging)",
        desc: "Filter by sender_domain_not_verified, bounce, auto-pause",
        url: "https://portal.azure.com/#resource/subscriptions/bf6c4494-5e1e-434e-97bf-2a92954c80c9/resourceGroups/Default-Web-EastUS/providers/microsoft.insights/components/348c91f0-6373-45ec-99c5-411452c1895b/logs",
        icon: "🔍"
      },
      {
        label: "Admin App Service (staging)",
        desc: "velaro-admin-staging · App Settings, logs, restarts",
        url: "https://portal.azure.com/#resource/subscriptions/bf6c4494-5e1e-434e-97bf-2a92954c80c9/resourceGroups/Default-Web-EastUS/providers/Microsoft.Web/sites/velaro-admin-staging/appServices",
        icon: "⚙️"
      },
    ],
    production: [
      {
        label: "ACS Communication Services (production)",
        desc: "velaro-comm-prod · v10 Production subscription",
        url: "https://portal.azure.com/#resource/subscriptions/6cbff6f7-18f3-425a-8755-ff9499ccbfa9/resourceGroups/Default-Web-EastUS/providers/Microsoft.Communication/CommunicationServices/velaro-comm-prod/overview",
        icon: "📡"
      },
      {
        label: "ACS Email Service (production)",
        desc: "velaro-email-prod · v10 Production subscription",
        url: "https://portal.azure.com/#resource/subscriptions/6cbff6f7-18f3-425a-8755-ff9499ccbfa9/resourceGroups/Default-Web-EastUS/providers/Microsoft.Communication/emailServices/velaro-email-prod/overview",
        icon: "📧"
      },
      {
        label: "Admin App Service (production)",
        desc: "velaro-admin-prod · App Settings, slots, logs",
        url: "https://portal.azure.com/#resource/subscriptions/6cbff6f7-18f3-425a-8755-ff9499ccbfa9/resourceGroups/Default-Web-EastUS/providers/Microsoft.Web/sites/velaro-admin-prod/appServices",
        icon: "⚙️"
      },
    ],
    runbooks: [
      {
        label: "Event Grid subscription command (run after prod deploy)",
        desc: "az eventgrid event-subscription create ... (copy from outbound-email-reputation-todo.md §7b)",
        url: "https://github.com/velaro/velaro-admin/blob/master/docs/outbound-email-reputation-todo.md",
        icon: "📋"
      },
      {
        label: "Superadmin outbound email setup guide",
        desc: "Enable per site, ACS provisioning, Event Grid, cost reference, diagnostics",
        url: "https://github.com/velaro/velaro-admin/blob/master/docs/superadmin-outbound-email-setup.md",
        icon: "📖"
      },
    ]
  };

  return (
    <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-base">🔗</span>
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Azure Quick Links — ACS, App Insights, Event Grid</span>
        </div>
        <span className="text-slate-400 text-xs">{open ? "▲ hide" : "▼ show"}</span>
      </button>

      {open && (
        <div className="p-4 bg-white space-y-4">
          {([["Staging", links.staging], ["Production", links.production], ["Runbooks & Docs", links.runbooks]] as const).map(([env, items]) => (
            <div key={env as string}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{env as string}</p>
              <div className="space-y-1.5">
                {(items as typeof links.staging).map(link => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2.5 px-3 py-2 rounded-md border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
                  >
                    <span className="text-base flex-shrink-0 mt-0.5">{link.icon}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-blue-700 group-hover:underline truncate">{link.label}</p>
                      <p className="text-xs text-slate-400">{link.desc}</p>
                    </div>
                    <svg className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-400 flex-shrink-0 mt-1 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
            <strong>Domain gate reminder:</strong> No customer can send a single campaign email without a verified sender domain (SPF + DKIM + DMARC). This is enforced in 3 places: schedule endpoint, template save, and dispatch service. It cannot be bypassed from the UI.
          </div>
        </div>
      )}
    </div>
  );
}

// ── ACS Email Cost Reference — shown in Campaign Settings card ─────────────────
const ACS_PRICE_PER_EMAIL = 0.00025; // $0.00025 per email (Azure Communication Services Email)
const TIERS = [
  { sends: 5_000,     suggestedCharge: 10  },
  { sends: 25_000,    suggestedCharge: 25  },
  { sends: 100_000,   suggestedCharge: 79  },
  { sends: 250_000,   suggestedCharge: 149 },
  { sends: 500_000,   suggestedCharge: 249 },
  { sends: 1_000_000, suggestedCharge: 449 },
];

function AcsCostReference() {
  const [open, setOpen] = React.useState(false);
  const fmt = (n: number) => n.toLocaleString("en-US");
  const dollar = (n: number) => "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-left"
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">ACS Email Cost Reference — Pricing Calculator</span>
        </div>
        <span className="text-slate-400 text-xs">{open ? "▲ hide" : "▼ show"}</span>
      </button>

      {open && (
        <div className="p-4 bg-white">
          <p className="text-xs text-slate-500 mb-3">
            Azure charges <strong>$0.00025 per email</strong> (¼ cent). Event Grid is free under 100K events/mo ($0.60/million after).
            The table below shows Velaro's raw ACS cost vs. suggested customer-facing price.
            Mailchimp charges $299/mo for 100K sends — our white-label margin is significant.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left px-3 py-2 border border-slate-200 text-slate-600 font-semibold">Sends / Month</th>
                  <th className="text-right px-3 py-2 border border-slate-200 text-slate-600 font-semibold">Velaro ACS Cost</th>
                  <th className="text-right px-3 py-2 border border-slate-200 text-slate-600 font-semibold">Per 1,000</th>
                  <th className="text-right px-3 py-2 border border-slate-200 text-green-700 font-semibold">Suggested Charge</th>
                  <th className="text-right px-3 py-2 border border-slate-200 text-green-700 font-semibold">Margin / Mo</th>
                  <th className="text-right px-3 py-2 border border-slate-200 text-slate-500 font-semibold">Mailchimp equiv.</th>
                </tr>
              </thead>
              <tbody>
                {TIERS.map((t, i) => {
                  const acsCost = t.sends * ACS_PRICE_PER_EMAIL;
                  const margin = t.suggestedCharge - acsCost;
                  const mailchimpPrices: Record<number,number> = {
                    5_000: 45, 25_000: 175, 100_000: 299, 250_000: 350, 500_000: 475, 1_000_000: 800
                  };
                  return (
                    <tr key={t.sends} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                      <td className="px-3 py-2 border border-slate-200 font-mono font-medium text-slate-700">{fmt(t.sends)}</td>
                      <td className="px-3 py-2 border border-slate-200 text-right text-orange-600 font-medium">{dollar(acsCost)}</td>
                      <td className="px-3 py-2 border border-slate-200 text-right text-slate-500">{dollar(acsCost / t.sends * 1000)}</td>
                      <td className="px-3 py-2 border border-slate-200 text-right text-green-700 font-semibold">${t.suggestedCharge}/mo</td>
                      <td className="px-3 py-2 border border-slate-200 text-right text-green-600 font-medium">{dollar(margin)}</td>
                      <td className="px-3 py-2 border border-slate-200 text-right text-slate-400">${mailchimpPrices[t.sends]}/mo</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3">
            <div className="bg-orange-50 border border-orange-100 rounded p-2 text-center">
              <div className="text-xs text-orange-500 font-medium">ACS rate</div>
              <div className="text-sm font-bold text-orange-700">$0.25 / 1,000</div>
              <div className="text-xs text-orange-400">or $250 / million</div>
            </div>
            <div className="bg-green-50 border border-green-100 rounded p-2 text-center">
              <div className="text-xs text-green-500 font-medium">Suggested rate to charge</div>
              <div className="text-sm font-bold text-green-700">$0.79–$3.00 / 1,000</div>
              <div className="text-xs text-green-400">3–12× markup</div>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded p-2 text-center">
              <div className="text-xs text-blue-500 font-medium">Mailchimp 100K</div>
              <div className="text-sm font-bold text-blue-700">$299/mo</div>
              <div className="text-xs text-blue-400">our cost: $25</div>
            </div>
          </div>

          <p className="text-xs text-slate-400 mt-3">
            To set a customer's quota: change "Max Emails / Month" above and save.
            Overage charges (billing to customer) are not yet automated — handled manually or via your billing system.
          </p>
        </div>
      )}
    </div>
  );
}

function RetentionLimitsSection({
  siteId, subscription, api, displayToast, onRefresh
}: {
  siteId: number; subscription: any; api: ReturnType<typeof useApi>;
  displayToast: (severity: any, message: string) => void; onRefresh: () => void;
}) {
  // Data Retention
  const [inboxDays, setInboxDays]       = React.useState<number>(subscription?.InboxRetentionDays ?? 365);
  const [minPurge, setMinPurge]         = React.useState<number>(subscription?.MinPurgeIntervalDays ?? 30);
  // Session Security
  const [sessionTimeout, setSessionTimeout] = React.useState<number>(subscription?.SessionTimeoutMinutes ?? 60);
  const [maxSessions, setMaxSessions]   = React.useState<number>(subscription?.MaxConcurrentSessions ?? 3);
  // Deployments
  const [maxDeployments, setMaxDeployments] = React.useState<number>(subscription?.MaxDeployments ?? 1);
  // AI / MCP
  const [maxMcpServers, setMaxMcpServers]   = React.useState<number>(subscription?.MaxMcpServers ?? 3);
  const [maxMcpPerConv, setMaxMcpPerConv]   = React.useState<number>(subscription?.MaxMcpCallsPerConversation ?? 20);
  const [maxMcpPerMonth, setMaxMcpPerMonth] = React.useState<number>(subscription?.MaxMcpCallsPerMonth ?? 1000);
  const [maxTurns, setMaxTurns]             = React.useState<number>(subscription?.MaxTurnsPerConversation ?? 0);
  const [turnWarnPct, setTurnWarnPct]       = React.useState<number>((subscription as any)?.TurnLimitWarnPercent ?? 0);
  const [botLimitAction, setBotLimitAction] = React.useState<string>((subscription as any)?.BotLimitAction ?? "close");
  const [botLimitMessage, setBotLimitMessage] = React.useState<string>((subscription as any)?.BotLimitMessage ?? "");
  const [maxConvPerHour, setMaxConvPerHour] = React.useState<number>((subscription as any)?.MaxVisitorConversationsPerHour ?? 0);
  // KB / AI Index
  const [kbReindexHours, setKbReindexHours] = React.useState<number>(subscription?.KbAutoReindexIntervalHours ?? 24);
  const [maxKbArticles, setMaxKbArticles]   = React.useState<number>(subscription?.MaxKbArticles ?? 100);
  // Contacts / CRM
  const [maxContacts, setMaxContacts]       = React.useState<number>(subscription?.MaxContacts ?? 2000);
  const [contactRetentionDays, setContactRetentionDays] = React.useState<number>(subscription?.ContactRetentionDays ?? 0);
  // Email Campaigns — max emails limit only (enable toggles moved to CampaignSettingsCard)
  const [maxEmails, setMaxEmails] = React.useState<number>(subscription?.MaxOutboundEmailsPerMonth ?? 1000);
  // Workflows
  const [maxWorkflows, setMaxWorkflows]     = React.useState<number>(subscription?.MaxActiveWorkflows ?? 10);
  // Reporting
  const [metricsRetention, setMetricsRetention] = React.useState<number>(subscription?.MetricsRetentionDays ?? 90);
  // API / Webhooks
  const [maxWebhooks, setMaxWebhooks]       = React.useState<number>(subscription?.MaxWebhookEndpoints ?? 5);

  const [saving, setSaving] = React.useState(false);


  const save = async () => {
    setSaving(true);
    try {
      const resp = await api.admin.put("DatabaseTool/RetentionLimits", {
        siteId,
        inboxRetentionDays: inboxDays,
        minPurgeIntervalDays: minPurge,
        sessionTimeoutMinutes: sessionTimeout,
        maxConcurrentSessions: maxSessions,
        maxDeployments,
        maxMcpServers,
        maxMcpCallsPerConversation: maxMcpPerConv,
        maxMcpCallsPerMonth: maxMcpPerMonth,
        maxTurnsPerConversation: maxTurns,
        turnLimitWarnPercent: turnWarnPct,
        botLimitAction: botLimitAction,
        botLimitMessage: botLimitMessage,
        maxVisitorConversationsPerHour: maxConvPerHour,
        kbAutoReindexIntervalHours: kbReindexHours,
        maxKbArticles,
        maxContacts,
        contactRetentionDays,
        maxOutboundEmailsPerMonth: maxEmails,
        maxActiveWorkflows: maxWorkflows,
        metricsRetentionDays: metricsRetention,
        maxWebhookEndpoints: maxWebhooks
      });
      if (resp.ok) { displayToast("success", "Subscription limits saved."); onRefresh(); }
      else { const err = await resp.json(); displayToast("error", err.error ?? "Save failed."); }
    } catch { displayToast("error", "Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mt-4">
      <h2 className="text-xl font-bold text-slate-800 mb-1">Per-Site Quotas &amp; Limits</h2>
      <p className="text-sm text-slate-500 mb-5">
        Numeric caps per site. <strong className="text-slate-600">0 = unlimited</strong> for most fields.
        Campaign enable/disable is in Subscription Settings above.
        Industry benchmarks shown in hints for reference.
      </p>

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Data Retention</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <LimitField label="Inbox Retention Window (days)" value={inboxDays} min={30} max={3650} onChange={setInboxDays} enforced={false}
          hint="How far back the inbox query window looks. Starter: 365, Enterprise: 730. Data stays in DB — use Data Retention feature to delete. Not yet query-gated." />
        <LimitField label="Min Purge Interval (days)" value={minPurge} min={1} max={365} onChange={setMinPurge} enforced={true}
          hint="30 = monthly (standard), 7 = weekly (premium), 1 = daily (enterprise). Requires EnableScheduledPurge. Checked by purge scheduler." />
      </div>

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Session Security</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <LimitField label="Session Timeout (minutes)" value={sessionTimeout} min={5} max={480} onChange={setSessionTimeout} enforced={true}
          hint="Auto-logout after inactivity. Requires EnableSessionTimeout. Industry std: 30 min (banking), 60 min (SaaS). Enforced by messaging session middleware." />
        <LimitField label="Max Concurrent Sessions" value={maxSessions} min={1} max={20} onChange={setMaxSessions} enforced={true}
          hint="Max logins per agent at once. Requires EnableConcurrentSessionLimit. Enforced at login. Banks use 1; SaaS default: 3." />
      </div>

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Deployments</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <LimitField label="Max Deployments" value={maxDeployments} min={1} max={2147483647} onChange={setMaxDeployments} enforced={true}
          hint="Chat widget embed codes per site. Free: 1, Starter: 3, Pro: 10, Enterprise: unlimited. Enforced in DeploymentController. Site 1032 = int.MaxValue at startup." />
      </div>

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">AI / MCP</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <LimitField label="Max MCP Servers" value={maxMcpServers} min={0} max={50} onChange={setMaxMcpServers} enforced={true}
          hint="External MCP tool servers. Enforced when adding servers. Free: 0, Starter: 3, Enterprise: unlimited. Requires EnableMcp." />
        <LimitField label="Max MCP Calls / Conversation" value={maxMcpPerConv} min={0} max={500} onChange={setMaxMcpPerConv} enforced={true}
          hint="Guards against runaway AI loops per chat. Enforced in AI conversation handler. Default: 20. 0 = unlimited (not recommended)." />
        <LimitField label="Max MCP Calls / Month" value={maxMcpPerMonth} min={0} max={1000000} onChange={setMaxMcpPerMonth} enforced={true}
          hint="Monthly budget. Enforced by MCP call counter — AI continues without tools when exhausted. Starter: 500, Pro: 5 000, Enterprise: 0." />
        <LimitField label="Max Turns / Conversation" value={maxTurns} min={0} max={10000} onChange={setMaxTurns} enforced={true}
          hint="Max visitor messages per conversation. 0 = unlimited. Enforced in WebMessageReceiver. Prevents bot abuse loops. Industry: Intercom charges per resolution, Tidio: 50 free AI convos/mo. Good bot: 8-15 turns. Suspicious: 50+." />
        <LimitField label="Turn Limit Warn % (0 = no warning)" value={turnWarnPct} min={0} max={99} onChange={setTurnWarnPct} enforced={true}
          hint="Warn visitor at this % of MaxTurns. e.g. 80 = warn when 80% used. 0 = no warning. The messenger shows an inline notice so the visitor isn't surprised." />
        <LimitField label="Max New Conversations / Visitor / Hour" value={maxConvPerHour} min={0} max={1000} onChange={setMaxConvPerHour} enforced={true}
          hint="Rate-limits new conversation starts per visitor. 0 = unlimited. Enforced in MessengerConversationsController. Prevents spammers from hammering the conversation creation endpoint." />
      </div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">At Turn Limit: Action</label>
          <select
            value={botLimitAction}
            onChange={e => setBotLimitAction(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-cornflower-blue-400 focus:border-transparent"
          >
            <option value="close">Close conversation (graceful resolve)</option>
            <option value="escalate">Escalate to human agent (fallback: close)</option>
          </select>
          <p className="text-xs text-slate-400 mt-1">What happens when MaxTurns is reached. Escalate routes to the conversation's team; falls back to close if no team.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">At Turn Limit: Visitor Message (empty = system default)</label>
          <input
            type="text"
            value={botLimitMessage}
            onChange={e => setBotLimitMessage(e.target.value)}
            placeholder="This conversation has reached its session limit. Please start a new conversation."
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-cornflower-blue-400 focus:border-transparent"
          />
          <p className="text-xs text-slate-400 mt-1">Message sent to visitor at the limit. Blank = friendly system default.</p>
        </div>
      </div>

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Knowledge Base / Unified Search Index</h3>
      <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-100 text-xs text-blue-800 leading-relaxed">
        <strong>Index technology:</strong> Azure AI Search — BM25 lexical matching (EnMicrosoft analyzer: stemming, stop words, linguistic normalization) + optional semantic re-ranker (Microsoft's cross-encoder re-scores top BM25 results — no custom embedding API calls, requires Standard tier).<br />
        <strong>Unified sources:</strong> <code className="bg-blue-100 px-1 rounded">kb_article</code> (curated KB) + <code className="bg-blue-100 px-1 rounded">scraper</code> (web pages from your scraper jobs) — all in one index. The bot queries both. Export scraper results from the AI Training page to also push them into the index.
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <LimitField label="Auto-Reindex Interval (hours)" value={kbReindexHours} min={0} max={168} onChange={setKbReindexHours} enforced={true}
          hint="0 = disabled (use on-save push only). KbReindexService runs every 30 min, triggers full reindex for sites where it's due. Starter: 24h, Pro: 4h, Enterprise: 1h. Catches bulk imports and scraper completions." />
        <LimitField label="Max KB Articles" value={maxKbArticles} min={0} max={100000} onChange={setMaxKbArticles} enforced={false}
          hint="Not yet enforced in KBArticlesController — store target for enforcement sprint. Zendesk free: 50, Help Scout: unlimited from $25/mo. Free: 25, Starter: 100, Pro: 500, Enterprise: 0." />
      </div>
      <ReindexNowButton siteId={siteId} api={api} displayToast={displayToast} />

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Contacts / CRM</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <LimitField label="Max Contacts" value={maxContacts} min={0} max={10000000} onChange={setMaxContacts} enforced={true}
          hint="Enforced by CampaignQuotaService (blocks CSV import + form capture). Also synced to SiteCampaignSettings on save. HubSpot free: 1M, Zoho free: 10K. Free: 500, Starter: 2K, Enterprise: 0." />
        <LimitField label="Contact Retention (days)" value={contactRetentionDays} min={0} max={3650} onChange={setContactRetentionDays} enforced={false}
          hint="0 = unlimited (no auto-purge). Not yet wired to a purge job — stored for future GDPR compliance sprint. GDPR max recommendation: 7 years (2 555 days)." />
      </div>

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Outbound Campaigns</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <LimitField label="Max Outbound Emails / Month" value={maxEmails} min={0} max={10000000} onChange={setMaxEmails} enforced={true}
          hint="Enforced by CampaignQuotaService — blocks sends when exceeded. 0 = unlimited. Default 5K. Higher sends = higher ACS cost (~$0.00028/email). Enable/disable toggle is in Subscription Settings above." />
      </div>

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Workflows</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <LimitField label="Max Active Workflows" value={maxWorkflows} min={0} max={10000} onChange={setMaxWorkflows} enforced={false}
          hint="Not yet enforced in WorkflowController — store target for enforcement sprint. Zapier free: 5, Make free: 2, HubSpot free: 5. Free: 3, Starter: 10, Enterprise: 0." />
        <LimitField label="Max Workflow Runs / Month" value={subscription?.MaxWorkflowRunsPerMonth ?? 0} min={0} max={1000000} onChange={() => {}} enforced={true} readOnly={true}
          hint="Enforced by WorkflowJobRunner. Read-only here — set per customer via Workflows billing page. 0 = unlimited. Zapier free: 100, Make free: 1K." />
      </div>

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Reporting / Metrics</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <LimitField label="Metrics Retention (days)" value={metricsRetention} min={7} max={3650} onChange={setMetricsRetention} enforced={false}
          hint="Not yet enforced — reports currently query all available data. Store target for enforcement sprint. Google Analytics free: 14 months, Zendesk: 12 months. Starter: 90, Pro: 365, Enterprise: 730." />
      </div>

      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">API / Webhooks</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <LimitField label="Max Webhook Endpoints" value={maxWebhooks} min={0} max={1000} onChange={setMaxWebhooks} enforced={false}
          hint="Not yet enforced in webhook config controller. Store target for enforcement sprint. Zapier: 5, HubSpot free: 5, Stripe: unlimited. Free: 1, Starter: 5, Pro: 25, Enterprise: 0." />
      </div>

      <div className="mt-2">
        <button onClick={save} disabled={saving}
          className="px-4 py-2 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50">
          {saving ? "Saving…" : "Save Quotas & Limits"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scraper Quota section — reads/writes ScraperConfigs table (AiTrainingContext)

function ScraperLimitsSection({
  siteId, api, displayToast
}: {
  siteId: number; api: ReturnType<typeof useApi>;
  displayToast: (severity: any, message: string) => void;
}) {
  const [maxJobsPerDay, setMaxJobsPerDay]       = React.useState(3);
  const [maxPagesPerJob, setMaxPagesPerJob]     = React.useState(200);
  const [maxPagesPerMonth, setMaxPagesPerMonth] = React.useState(1000);
  const [loading, setLoading]                   = React.useState(false);
  const [saving, setSaving]                     = React.useState(false);

  React.useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    api.admin.get(`DatabaseTool/ScraperConfig/${siteId}`).then(r => r.json()).then(d => {
      setMaxJobsPerDay(d.maxJobsPerDay ?? 3);
      setMaxPagesPerJob(d.maxPagesPerJob ?? 200);
      setMaxPagesPerMonth(d.maxPagesPerMonth ?? 1000);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [siteId, api]);

  const save = async () => {
    setSaving(true);
    try {
      const resp = await api.admin.put("DatabaseTool/ScraperConfig", { siteId, maxJobsPerDay, maxPagesPerJob, maxPagesPerMonth });
      if (resp.ok) displayToast("success", "Scraper limits saved.");
      else { const err = await resp.json(); displayToast("error", err.error ?? "Save failed."); }
    } catch { displayToast("error", "Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mt-4">
      <h2 className="text-xl font-bold text-slate-800 mb-1">Web Scraper Quotas</h2>
      <p className="text-sm text-slate-500 mb-5">
        Controls how much web scraping a site can do per day and per month.
        Scraper is a metered compute resource — set limits per plan tier.
        <br />
        <span className="font-medium">Defaults:</span> 3 jobs/day · 200 pages/job · 1,000 pages/month.
        Site 1032 (Velaro internal): set to high values for testing.
      </p>
      {loading ? (
        <p className="text-sm text-slate-400">Loading...</p>
      ) : (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <LimitField label="Max Jobs / Day" value={maxJobsPerDay} min={0} max={9999} onChange={setMaxJobsPerDay}
            hint="Jobs created per calendar day (UTC). Resets at midnight." />
          <LimitField label="Max Pages / Job" value={maxPagesPerJob} min={1} max={100000} onChange={setMaxPagesPerJob}
            hint="Cap on a single scraper job. Frontend request is capped at this value." />
          <LimitField label="Max Pages / Month" value={maxPagesPerMonth} min={0} max={10000000} onChange={setMaxPagesPerMonth}
            hint="Total pages across all jobs in a calendar month. 0 = unlimited." />
        </div>
      )}
      <button onClick={save} disabled={saving || loading}
        className="px-4 py-2 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50">
        {saving ? "Saving..." : "Save Scraper Limits"}
      </button>
    </div>
  );
}

